import { useEffect, useMemo, useState } from 'react';

/**
 * Edit-mode is enabled when the URL has `?adminEdit=1` (or `=true`).
 * When enabled the storefront renders <Editable> elements as click-to-edit.
 * In all other cases the storefront renders normally for shoppers.
 *
 * Live URL changes (e.g. admin toggling the flag from a parent window) are
 * picked up via popstate so the storefront flips modes without a hard reload.
 */
export function useEditMode(): { editing: boolean } {
  const [editing, setEditing] = useState<boolean>(() => readFlag());

  useEffect(() => {
    const onChange = () => setEditing(readFlag());
    window.addEventListener('popstate', onChange);
    window.addEventListener('hashchange', onChange);
    // Cross-frame toggle: parent admin can postMessage({ type:'edit-mode', value:true })
    const onMessage = (e: MessageEvent) => {
      if (e?.data?.type === 'edit-mode' && typeof e.data.value === 'boolean') {
        const url = new URL(window.location.href);
        if (e.data.value) url.searchParams.set('adminEdit', '1');
        else url.searchParams.delete('adminEdit');
        window.history.replaceState({}, '', url.toString());
        setEditing(e.data.value);
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('popstate', onChange);
      window.removeEventListener('hashchange', onChange);
      window.removeEventListener('message', onMessage);
    };
  }, []);

  return useMemo(() => ({ editing }), [editing]);
}

function readFlag(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const v = params.get('adminEdit');
  return v === '1' || v === 'true';
}
